/**
 * Shared design-system `sx` tokens for MUI surfaces, matching the sm4rt-web
 * front-end conventions (see the `sm4rt-web-ui` skill / Work Item Dashboard).
 *
 * Import these instead of hand-rolling colours/radii per page so every form,
 * table, and toolbar page reads consistently. Accent = #4A90D9.
 *
 * Plain objects so they compose either by spreading (`{ ...CARD_SX, p: 2 }`) or
 * via the sx array form (`sx={[FIELD_SX, { width: 160 }]}`).
 */

const ACCENT = '#4A90D9';
const ACCENT_HOVER = '#3B82C4';

/**
 * White card floating on the grey page surface (#f0f4f9). Matches the shared
 * floating toolbar (`.barFloating`) exactly so a toolbar and the card beneath
 * it read as one system: 10px radius, soft shadow + a hairline ring (no solid
 * border). This is THE canonical floating-card style — see the sm4rt-web-ui
 * skill §3/§4.
 */
export const CARD_SX = {
  bgcolor: '#fff',
  borderRadius: '10px',
  boxShadow: 'var(--card-shadow)',
};

/** Primary (contained) button — accent fill. */
export const PRIMARY_BTN_SX = {
  boxShadow: 'none',
  bgcolor: ACCENT,
  '&:hover': { bgcolor: ACCENT_HOVER, boxShadow: 'none' },
};

/** Secondary (outlined) button — accent outline. */
export const OUTLINED_BTN_SX = {
  color: ACCENT,
  borderColor: ACCENT,
  '&:hover': { borderColor: ACCENT_HOVER, bgcolor: '#EDF5FF', color: ACCENT_HOVER },
};

/** Accent-coloured link. */
export const ACCENT_LINK_SX = { color: ACCENT };

/**
 * MUI text inputs / Autocomplete / date fields aligned to the design system:
 * hairline #d0dcea border, 6px radius, the quiet dashboard focus (accent
 * border-colour change, no glow ring), #646464 labels, #bbb placeholder.
 * Apply via `sx` on a TextField, or on an Autocomplete (the selectors are
 * descendants of its root). Value text is 14px from the theme, not here.
 */
export const FIELD_SX = {
  '& .MuiOutlinedInput-root': { borderRadius: '6px', backgroundColor: '#fff' },
  '& .MuiInputBase-input': { color: '#1a1a1a' },
  '& .MuiInputBase-input::placeholder': { color: '#bbb', opacity: 1 },
  '& .MuiInputLabel-root': { color: '#646464' },
  '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d0dcea' },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#c5d9f0' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: ACCENT,
    borderWidth: 1,
  },
};

/** MUI Checkbox with the accent checked colour. */
export const CHECKBOX_SX = { '&.Mui-checked': { color: ACCENT } };

/**
 * Compact secondary (outlined) header-action button — accent text, `#c8d4e0`
 * hairline, soft accent hover (skill §4/§9). Lighter padding/font than MUI's
 * default `size="small"` outlined button, for the action row in a detail
 * page's header card.
 */
export const COMPACT_BTN_SX = {
  fontSize: 13,
  fontWeight: 500,
  py: 0.25,
  px: 1.25,
  minHeight: 0,
  color: ACCENT,
  borderColor: '#c8d4e0',
  '&:hover': { borderColor: ACCENT, bgcolor: '#EDF5FF' },
  '& .MuiButton-startIcon': { mr: 0.5 },
};

/** Same treatment for the buttons inside a segmented `ButtonGroup`
 *  (ButtonGroup handles the shared border / radius collapsing itself). */
export const COMPACT_BTN_GROUP_SX = {
  '& .MuiButtonGroup-grouped': COMPACT_BTN_SX,
};
